import React from 'react';
import { FileCheck, Building, Clock, CheckCircle, Shield, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHelmet from '../components/SEOHelmet';
import Breadcrumbs from '../components/Breadcrumbs';

const CountyRecordingPage = () => {
  const countyRecordingSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "County Recording Services California",
    "description": "Professional county recording services throughout California. Real estate documents, liens, notarizations, and official recordings at all California county offices.",
    "provider": {
      "@type": "LegalService",
      "name": "Legal Document Specialist",
      "telephone": "(760) 866-9182",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "225 South Civic Drive, Ste 2-16",
        "addressLocality": "Palm Springs",
        "addressRegion": "CA",
        "postalCode": "92262",
        "addressCountry": "US"
      }
    },
    "areaServed": {
      "@type": "State",
      "name": "California"
    },
    "availableChannel": {
      "@type": "ServiceChannel",
      "availableLanguage": "English",
      "servicePhone": "(760) 866-9182",
      "serviceUrl": `${window.location.origin}/county-recording`
    },
    "offers": [
      {
        "@type": "Offer",
        "name": "Real Estate Recording",
        "description": "Deeds, mortgages, and property documents",
      },
      {
        "@type": "Offer",
        "name": "Lien Recording",
        "description": "Mechanics liens and judgment liens",
      }
    ]
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What documents can be recorded at California county recorders?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "California county recorders accept property deeds (grant deeds, quitclaim deeds), deeds of trust, mortgages, liens (mechanics liens, judgment liens, tax liens), UCC filings, business documents (articles of incorporation, partnership agreements), and various other legal documents. All documents must meet California recording requirements including proper notarization and standard formatting."
        }
      },
      {
        "@type": "Question",
        "name": "How long does county recording take in California?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Standard recording typically takes 2-3 business days, priority recording 1 business day (next day), and same-day recording is available for urgent matters. Processing times may vary by county and document complexity. Once recorded, documents are entered into the public record system and a stamped copy is returned."
        }
      },
      {
        "@type": "Question",
        "name": "Do documents need to be notarized before recording?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, most documents requiring recording in California must be notarized. Deeds must be personally signed by the owner(s) before a California notary public. The notary verifies the identity of signers and ensures proper execution. We offer notary services as part of our comprehensive county recording solutions."
        }
      },
      {
        "@type": "Question",
        "name": "How much does county recording cost in California?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Recording costs include county filing fees plus our service fees. County fees vary by location and are additional to service fees. Email your case specifics to quote@legaldocspecialist.com for a quote based on your specific needs."
        }
      },
      {
        "@type": "Question",
        "name": "Can you record documents in all California counties?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, we provide county recording services in all 58 California counties. Our statewide coverage includes major counties like Riverside, San Bernardino, Los Angeles, Orange, and San Diego, as well as all smaller counties throughout the state. We have established relationships with county recorders offices statewide."
        }
      },
      {
        "@type": "Question",
        "name": "What are California deed recording requirements?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "California deeds must be in standard format (8.5 x 11 inches), properly notarized with signatures verified, include complete legal descriptions of the property, contain accurate grantor and grantee information, and include the assessor's parcel number. Documents must have proper margins and formatting to meet county recorder standards. Our team ensures all documents meet these requirements before submission."
        }
      }
    ]
  };

  React.useEffect(() => {
    const serviceScript = document.createElement('script');
    serviceScript.type = 'application/ld+json';
    serviceScript.textContent = JSON.stringify(countyRecordingSchema);
    document.head.appendChild(serviceScript);

    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    return () => {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => {
        if (script.textContent === JSON.stringify(countyRecordingSchema) || 
            script.textContent === JSON.stringify(faqSchema)) {
          script.remove();
        }
      });
    };
  }, []);

  return (
    <div>
      <SEOHelmet 
        title="County Recording California | Real Estate Recording & Liens | Legal Document Specialist"
        description="Professional county recording services throughout California. Real estate documents, liens, notarizations, and official recordings. Same-day service available. Request a quote online."
        keywords="county recording California, real estate recording, deed recording, lien recording, notary services, document recording, property documents"
        canonical="/county-recording"
      />
      <Header />
      <Breadcrumbs />

      {/* Hero Section */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '160px 0 80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <h1 className="brand-display" style={{
              marginBottom: '1.5rem',
              color: 'var(--text-primary)'
            }}>
              California County Recording Services
            </h1>
            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              marginBottom: '2rem',
              lineHeight: '1.6'
            }}>
              Professional county recording services throughout California. Real estate documents, liens, notarizations, and official recordings at all California county offices.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/quote?service=county-recording" className="btn-primary" data-testid="hero-quote-link">
                Get Recording Quote
              </Link>
              <a href="mailto:support@legaldocspecialist.com" className="btn-secondary">
                Email Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Recording Services */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              County Recording Services
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Comprehensive document recording services for all California counties
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem'
          }}>
            {[
              {
                name: 'Real Estate Recording',
                icon: <Building size={28} color="white" />,
                description: 'Property deeds, mortgages, and real estate documents',
                features: ['Grant deeds', 'Quitclaim deeds', 'Deeds of trust', 'Mortgage documents'],
              },
              {
                name: 'Lien Recording',
                icon: <FileCheck size={28} color="white" />,
                description: 'Mechanics liens, judgment liens, and other encumbrances',
                features: ['Mechanics liens', 'Judgment liens', 'Tax liens', 'UCC filings'],
              },
              {
                name: 'Business Documents',
                icon: <Shield size={28} color="white" />,
                description: 'Corporate filings and business-related recordings',
                features: ['Articles of incorporation', 'Partnership agreements', 'Operating agreements', 'Trademark filings'],
              },
              {
                name: 'Notary Services',
                icon: <FileCheck size={28} color="white" />,
                description: 'Notarization and authentication services',
                features: ['Document notarization', 'Signature witnessing', 'Oath administration', 'Acknowledgments'],
              }
            ].map((service, index) => (
              <div key={index} style={{
                backgroundColor: 'var(--bg-page)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  backgroundColor: 'var(--brand-primary)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem'
                }}>
                  {service.icon}
                </div>
                
                <h3 className="heading-3" style={{ marginBottom: '0.5rem' }}>
                  {service.name}
                </h3>
                
                <Link to="/quote?service=county-recording" data-testid={`quote-link-${index}`} style={{ display: 'inline-block', fontSize: '0.95rem', fontWeight: '600', color: 'var(--brand-primary)', marginBottom: '1rem', textDecoration: 'underline' }}>Request a Quote</Link>
                
                <p className="body-small" style={{
                  color: 'var(--text-secondary)',
                  marginBottom: '1.5rem'
                }}>
                  {service.description}
                </p>
                
                <ul style={{
                  textAlign: 'left',
                  paddingLeft: '0',
                  listStyle: 'none'
                }}>
                  {service.features.map((feature, fIndex) => (
                    <li key={fIndex} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.5rem',
                      fontSize: '0.9rem',
                      color: 'var(--text-secondary)'
                    }}>
                      <CheckCircle size={16} color="var(--brand-primary)" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Counties Served */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              California Counties We Serve
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Professional recording services available in all 58 California counties
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem'
          }}>
            {[
              'Riverside County', 'San Bernardino County', 'Los Angeles County', 'Orange County',
              'San Diego County', 'Imperial County', 'Kern County', 'Ventura County',
              'Santa Barbara County', 'Fresno County', 'Kings County', 'Tulare County',
              'Inyo County', 'Mono County', 'Madera County', 'Merced County',
              'Stanislaus County', 'San Joaquin County', 'Alameda County', 'Contra Costa County',
              'Solano County', 'Napa County', 'Sonoma County', 'Marin County'
            ].map((county, index) => (
              <div key={index} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem',
                backgroundColor: 'var(--bg-card)',
                borderRadius: '6px',
                border: '1px solid var(--border-light)'
              }}>
                <MapPin size={16} color="var(--brand-primary)" />
                <span style={{ color: 'var(--text-primary)', fontSize: '0.9rem' }}>
                  {county}
                </span>
              </div>
            ))}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              padding: '0.75rem',
              backgroundColor: 'var(--brand-primary)',
              borderRadius: '6px',
              color: 'white',
              fontWeight: '600'
            }}>
              + All 58 California Counties
            </div>
          </div>
        </div>
      </section>

      {/* Understanding County Recording */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '2rem', textAlign: 'center' }}>
              Understanding California County Recording
            </h2>
            
            <p className="body-medium" style={{ 
              color: 'var(--text-secondary)', 
              marginBottom: '2rem',
              textAlign: 'center',
              lineHeight: '1.8'
            }}>
              County recorders in California are responsible for maintaining public records of real property transactions, ensuring legal documentation of ownership transfers, liens, and other critical documents affecting property rights.
            </p>

            <div style={{
              backgroundColor: 'var(--bg-card)',
              padding: '2.5rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)',
              marginBottom: '2rem'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>
                Key Responsibilities of County Recorders
              </h3>
              <ul style={{
                paddingLeft: '0',
                listStyle: 'none'
              }}>
                {[
                  'Recording Property Deeds: Ensures property transfers are officially documented and maintains the chain of title',
                  'Recording Mortgages and Deeds of Trust: Establishes legal interests for lenders and protects them through public recognition',
                  'Recording Liens and Releases: Includes tax liens, mechanics liens, and judgment liens with proper release documentation',
                  'Maintaining Public Access: Provides searchable records for buyers, sellers, and attorneys to confirm ownership and uncover encumbrances',
                  'Document Verification: Checks signatures, notarization, formatting, and compliance with state recording requirements',
                  'Chain of Title Preservation: Maintains historical records ensuring proper documentation of all property transfers'
                ].map((responsibility, index) => (
                  <li key={index} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                    marginBottom: '1rem',
                    color: 'var(--text-secondary)'
                  }}>
                    <CheckCircle size={20} color="var(--brand-primary)" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span>{responsibility}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Recording Process */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Professional Recording Process
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Streamlined document recording from preparation to completion
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '2rem',
            maxWidth: '1000px',
            margin: '0 auto'
          }}>
            {[
              {
                step: '1',
                title: 'Document Preparation',
                description: 'We review documents for compliance with California requirements including proper notarization, standard formatting (8.5 x 11 inches), complete legal descriptions, and accurate grantor/grantee information.'
              },
              {
                step: '2',
                title: 'Verification & Review',
                description: 'Our team verifies all signatures, notarization authenticity, appropriate fees, and ensures documents meet county-specific recording standards before submission.'
              },
              {
                step: '3',
                title: 'County Submission',
                description: 'Documents are submitted to the appropriate county recorder\'s office via in-person delivery, mail, or electronic filing depending on urgency and county capabilities.'
              },
              {
                step: '4',
                title: 'Recording & Return',
                description: 'Once verified and recorded, the document enters the public record system. A copy with the official recording stamp is returned to you with confirmation of recording date and details.'
              }
            ].map((item, index) => (
              <div key={index} style={{
                backgroundColor: 'var(--bg-page)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  backgroundColor: 'var(--brand-primary)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem',
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  color: 'white'
                }}>
                  {item.step}
                </div>
                <h3 className="heading-4" style={{ marginBottom: '1rem' }}>
                  {item.title}
                </h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Timelines */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Recording Timelines
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Flexible service options to meet your recording deadlines
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '2rem'
          }}>
            {[
              {
                name: 'Standard Recording',
                timeline: '2-3 Business Days',
                description: 'Regular recording for non-urgent documents'
              },
              {
                name: 'Priority Recording',
                timeline: 'Next Business Day',
                description: 'Expedited recording for time-sensitive matters'
              },
              {
                name: 'Same-Day Recording',
                timeline: 'Same Day',
                description: 'Emergency recording for critical deadlines'
              }
            ].map((service, index) => (
              <div key={index} style={{
                backgroundColor: 'var(--bg-card)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  backgroundColor: 'var(--brand-primary)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem'
                }}>
                  <Clock size={24} color="white" />
                </div>
                
                <h3 className="heading-3" style={{ marginBottom: '0.5rem' }}>
                  {service.name}
                </h3>
                
                <p style={{
                  color: 'var(--brand-primary)',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  marginBottom: '1rem'
                }}>
                  {service.timeline}
                </p>
                
                <p className="body-small" style={{
                  color: 'var(--text-secondary)'
                }}>
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Best Practices */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '2rem', textAlign: 'center' }}>
              Recording Best Practices for 2025
            </h2>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '2rem',
              marginTop: '3rem'
            }}>
              {[
                {
                  title: 'Proper Document Preparation',
                  description: 'Ensure all legal requirements are met including notarization, standard page sizes, complete legal descriptions, and accurate party information before submission.'
                },
                {
                  title: 'Notarization Compliance',
                  description: 'Use California-authorized notaries familiar with state regulations. Verify proper identification and ensure all notarial acts comply with California notary laws.'
                },
                {
                  title: 'Format Standards',
                  description: 'Documents must be 8.5 x 11 inches (larger sizes incur additional fees), with proper margins, legible text, and professional formatting meeting county standards.'
                },
                {
                  title: 'Fee Verification',
                  description: 'County recording fees vary by location and document type. Verify exact fees with the county recorder before submission to avoid delays or rejections.'
                },
                {
                  title: 'Submission Methods',
                  description: 'Choose appropriate submission: in-person for immediate needs, mail for standard processing, or electronic filing where available for faster turnaround.'
                },
                {
                  title: 'Review and Verification',
                  description: 'Always verify document acceptance and review returned copies for accurate recording information, stamps, and any notations from the county recorder.'
                }
              ].map((practice, index) => (
                <div key={index} style={{
                  backgroundColor: 'var(--bg-page)',
                  padding: '2rem',
                  borderRadius: '12px',
                  border: '1px solid var(--border-medium)'
                }}>
                  <h3 className="heading-4" style={{ marginBottom: '1rem', color: 'var(--brand-primary)' }}>
                    {practice.title}
                  </h3>
                  <p className="body-small" style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                    {practice.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related Resources Section */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '60px 0' }}>
        <div className="container">
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
            textAlign: 'center'
          }}>
            <h3 className="heading-2" style={{ marginBottom: '1rem' }}>
              Complete Legal Document Solutions
            </h3>
            <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '2rem', lineHeight: '1.8' }}>
              County recording is essential for real estate transactions. Discover our comprehensive suite of legal document services for California law firms.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link 
                to="/resources/california-court-procedures-guide"
                style={{
                  padding: '1rem 2rem',
                  backgroundColor: 'var(--brand-primary)',
                  color: 'white',
                  borderRadius: '6px',
                  textDecoration: 'none',
                  fontWeight: '600'
                }}
              >
                Court Procedures Guide →
              </Link>
              <Link 
                to="/resources/legal-document-services-guide"
                style={{
                  padding: '1rem 2rem',
                  backgroundColor: 'transparent',
                  color: 'var(--brand-primary)',
                  border: '2px solid var(--brand-primary)',
                  borderRadius: '6px',
                  textDecoration: 'none',
                  fontWeight: '600'
                }}
              >
                View All Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '3rem', textAlign: 'center' }}>
              Frequently Asked Questions
            </h2>

            {[
              {
                question: 'What documents can be recorded at California county recorders?',
                answer: 'California county recorders accept property deeds (grant deeds, quitclaim deeds), deeds of trust, mortgages, liens (mechanics liens, judgment liens, tax liens), UCC filings, business documents (articles of incorporation, partnership agreements), and various other legal documents. All documents must meet California recording requirements including proper notarization and standard formatting.'
              },
              {
                question: 'How long does county recording take in California?',
                answer: 'Standard recording typically takes 2-3 business days, priority recording 1 business day (next day), and same-day recording is available for urgent matters. Processing times may vary by county and document complexity. Once recorded, documents are entered into the public record system and a stamped copy is returned.'
              },
              {
                question: 'Do documents need to be notarized before recording?',
                answer: 'Yes, most documents requiring recording in California must be notarized. Deeds must be personally signed by the owner(s) before a California notary public. The notary verifies the identity of signers and ensures proper execution. We offer notary services as part of our comprehensive county recording solutions.'
              },
              {
                question: 'How much does county recording cost in California?',
                answer: 'Recording costs include county filing fees plus our service fees. County fees vary by location and are additional to service fees. Email your case specifics to quote@legaldocspecialist.com for a quote based on your specific needs.'
              },
              {
                question: 'Can you record documents in all California counties?',
                answer: 'Yes, we provide county recording services in all 58 California counties. Our statewide coverage includes major counties like Riverside, San Bernardino, Los Angeles, Orange, and San Diego, as well as all smaller counties throughout the state. We have established relationships with county recorders offices statewide.'
              },
              {
                question: 'What are California deed recording requirements?',
                answer: 'California deeds must be in standard format (8.5 x 11 inches), properly notarized with signatures verified, include complete legal descriptions of the property, contain accurate grantor and grantee information, and include the assessor\'s parcel number. Documents must have proper margins and formatting to meet county recorder standards. Our team ensures all documents meet these requirements before submission.'
              }
            ].map((faq, index) => (
              <div key={index} style={{
                backgroundColor: 'var(--bg-card)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)',
                marginBottom: '1.5rem'
              }}>
                <h3 className="heading-4" style={{ marginBottom: '1rem', color: 'var(--brand-primary)' }}>
                  {faq.question}
                </h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        backgroundColor: 'var(--brand-primary)',
        padding: '80px 0',
        color: 'white'
      }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 className="heading-1" style={{ marginBottom: '1rem', color: 'white' }}>
            Need County Recording Services?
          </h2>
          <p className="body-large" style={{ marginBottom: '2rem', opacity: 0.9 }}>
            Professional document recording services throughout all California counties
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link 
              to="/place-order"
              style={{
                padding: '1rem 2rem',
                backgroundColor: 'white',
                color: 'var(--brand-primary)',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: '600'
              }}
            >
              Place Order Now
            </Link>
            <a href="/quote"
              style={{
                padding: '1rem 2rem',
                backgroundColor: 'transparent',
                color: 'white',
                border: '2px solid white',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: '600'
              }}
            >
              Request a Quote
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CountyRecordingPage;